import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Search, Send } from 'lucide-react'
import {
  ClientMessageRow,
  fetchAllClientMessageThreads,
  fetchAllPreferences,
  markClientMessagesReadByCoach,
  sendCoachMessage,
} from '../../lib/instantData'
import { isAdminEmail } from '../../lib/adminEmails'
import { db } from '../../lib/db'
import GlassCard from '../../components/ui/GlassCard'
import GradientButton from '../../components/ui/GradientButton'
import PanelShell from '../../components/ui/PanelShell'

interface Thread {
  userId: string
  messages: ClientMessageRow[]
  lastMessage: ClientMessageRow
  unreadCount: number
}

interface ClientInfo {
  fullName: string
  email: string
}

const formatTimestamp = (createdAt: number) => {
  const date = new Date(createdAt)
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  if (sameDay) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const AdminClientMessages: React.FC = () => {
  const navigate = useNavigate()
  const { user: instantUser } = db.useAuth()
  const token = instantUser?.refresh_token
  const [threads, setThreads] = useState<Thread[]>([])
  const [clientInfo, setClientInfo] = useState<Record<string, ClientInfo>>({})
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevMessageCountRef = useRef(0)
  const prevSelectedUserIdRef = useRef<string | null>(null)

  const loadThreads = useCallback(async (opts?: { silent?: boolean }) => {
    if (!token) return
    if (!opts?.silent) setLoading(true)
    try {
      const [threadRows, preferences] = await Promise.all([
        fetchAllClientMessageThreads(token),
        fetchAllPreferences(),
      ])
      setThreads(threadRows)

      const info: Record<string, ClientInfo> = {}
      preferences
        .filter((p) => !isAdminEmail(p.email))
        .forEach((p) => {
          info[p.user_id] = { fullName: p.full_name || 'Unknown Client', email: p.email }
        })
      setClientInfo(info)
    } catch (err) {
      console.error('Failed to load client message threads:', err)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    if (!token) return
    loadThreads()
    const interval = setInterval(() => loadThreads({ silent: true }), 8000)
    return () => clearInterval(interval)
  }, [loadThreads, token])

  useEffect(() => {
    // Only auto-scroll when switching to a different conversation, or when
    // a new message actually arrives in the open one — not on every
    // background refresh, otherwise it fights with scrolling up to read
    // earlier messages.
    const count = threads.find((t) => t.userId === selectedUserId)?.messages.length || 0
    const changedThread = prevSelectedUserIdRef.current !== selectedUserId
    if (changedThread || count > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessageCountRef.current = count
    prevSelectedUserIdRef.current = selectedUserId
  }, [selectedUserId, threads])

  const selectThread = async (userId: string) => {
    setSelectedUserId(userId)
    if (!token) return
    try {
      await markClientMessagesReadByCoach(userId, token)
      loadThreads({ silent: true })
    } catch (err) {
      console.error('Failed to mark messages read:', err)
    }
  }

  const handleReply = async () => {
    if (!reply.trim() || !selectedUserId || !token) return
    setSending(true)
    try {
      await sendCoachMessage(
        {
          userId: selectedUserId,
          senderName: 'Cassie',
          content: reply.trim(),
        },
        token
      )
      setReply('')
      await loadThreads({ silent: true })
    } catch (err) {
      console.error('Failed to send reply:', err)
    } finally {
      setSending(false)
    }
  }

  const filteredThreads = threads.filter((thread) => {
    const info = clientInfo[thread.userId]
    if (!searchTerm) return true
    const haystack = `${info?.fullName || ''} ${info?.email || ''}`.toLowerCase()
    return haystack.includes(searchTerm.toLowerCase())
  })

  const selectedThread = threads.find((t) => t.userId === selectedUserId)
  const selectedInfo = selectedUserId ? clientInfo[selectedUserId] : undefined
  const totalUnread = threads.reduce((sum, t) => sum + t.unreadCount, 0)

  return (
    <PanelShell
      eyebrow="Admin"
      title="Client Messages"
      subtitle={`${totalUnread} unread · ${threads.length} conversation${threads.length === 1 ? '' : 's'}`}
      actions={
        <>
          <GradientButton
            onClick={() => loadThreads()}
            variant="outline"
            size="sm"
            icon={RefreshCw}
          >
            Refresh
          </GradientButton>
          <GradientButton onClick={() => navigate('/admin/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
            Admin home
          </GradientButton>
        </>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gold" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[70vh] min-h-[500px]">
          <GlassCard hover={false} padded={false} className="lg:col-span-1 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-9 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredThreads.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">
                  No conversations yet.
                </div>
              ) : (
                filteredThreads.map((thread) => {
                  const info = clientInfo[thread.userId]
                  const isSelected = selectedUserId === thread.userId
                  return (
                    <button
                      key={thread.userId}
                      onClick={() => selectThread(thread.userId)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${
                        isSelected ? 'bg-brand-soft' : 'hover:bg-cream'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-ink truncate">
                          {info?.fullName || 'Unknown Client'}
                        </span>
                        {thread.unreadCount > 0 && (
                          <span className="flex-shrink-0 text-xs font-semibold bg-red-500 text-white rounded-full px-2 py-0.5">
                            {thread.unreadCount}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {thread.lastMessage.senderRole === 'coach' ? 'You: ' : ''}
                        {thread.lastMessage.content}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimestamp(thread.lastMessage.createdAt)}
                      </p>
                    </button>
                  )
                })
              )}
            </div>
          </GlassCard>

          <GlassCard hover={false} padded={false} className="lg:col-span-2 overflow-hidden flex flex-col">
            {!selectedThread ? (
              <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                Select a conversation to view messages.
              </div>
            ) : (
              <>
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="font-display text-lg font-semibold text-ink">
                    {selectedInfo?.fullName || 'Unknown Client'}
                  </h3>
                  <p className="text-xs text-gray-500">{selectedInfo?.email}</p>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                  {selectedThread.messages.map((message) => {
                    const isCoach = message.senderRole === 'coach'
                    return (
                      <div key={message.id} className={`flex ${isCoach ? 'justify-end' : 'justify-start'}`}>
                        <div className="max-w-md flex flex-col">
                          <div
                            className={`px-4 py-3 rounded-2xl shadow-sm ${
                              isCoach
                                ? 'bg-ink text-white'
                                : 'bg-cream text-textPrimary border border-gray-100'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">{message.content}</p>
                          </div>
                          <span className={`text-xs text-gray-400 mt-1 px-1 ${isCoach ? 'text-right' : ''}`}>
                            {formatTimestamp(message.createdAt)}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef} />
                </div>
                <div className="px-6 py-4 border-t border-gray-100">
                  <div className="flex items-end gap-3">
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Reply as Cassie..."
                      rows={2}
                      className="form-input flex-1 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleReply()
                        }
                      }}
                      disabled={sending}
                    />
                    <GradientButton onClick={handleReply} disabled={!reply.trim() || sending} icon={Send} size="sm">
                      {sending ? 'Sending...' : 'Send'}
                    </GradientButton>
                  </div>
                </div>
              </>
            )}
          </GlassCard>
        </div>
      )}
    </PanelShell>
  )
}

export default AdminClientMessages
